import { useCallback, useEffect } from 'react';

import { format, subDays } from 'date-fns';

import { cleaningReportDefaultShortcut } from './cleaningReportUtils';

export function useCleaningReportCurrShortcutSync(
    draftPeriod,
    setCurrShortcut
) {
    useEffect(() => {
        setCurrShortcut(
            cleaningReportDefaultShortcut(
                draftPeriod.startDate,
                draftPeriod.endDate
            )
        );
    }, [draftPeriod.startDate, draftPeriod.endDate, setCurrShortcut]);
}

/**
 * Handlers de período compartilhados (ReportHeader + atalhos).
 * @param {(startYmd: string, endYmd: string) => unknown} fetchForRange
 */
export function useCleaningReportPeriodHandlers(
    draftPeriod,
    setDraftPeriod,
    periodDates,
    setPeriodDates,
    fetchForRange
) {
    const applyPeriod = useCallback(() => {
        const next = { ...draftPeriod };
        if (
            periodDates.startDate === next.startDate &&
            periodDates.endDate === next.endDate
        ) {
            fetchForRange(next.startDate, next.endDate);
        } else {
            setPeriodDates(next);
        }
    }, [
        draftPeriod,
        periodDates.startDate,
        periodDates.endDate,
        fetchForRange,
        setPeriodDates,
    ]);

    const handleCleaningFromDateChange = useCallback(
        date => {
            if (!date) return;
            setDraftPeriod(d => ({
                ...d,
                startDate: format(date, 'yyyy-MM-dd'),
            }));
        },
        [setDraftPeriod]
    );

    const handleCleaningUntilDateChange = useCallback(
        date => {
            if (!date) return;
            setDraftPeriod(d => ({
                ...d,
                endDate: format(date, 'yyyy-MM-dd'),
            }));
        },
        [setDraftPeriod]
    );

    const handleCleaningPeriodShortcut = useCallback(
        shortcut => {
            const newUntil = new Date();
            const untilYmd = format(newUntil, 'yyyy-MM-dd');
            let next = null;
            switch (shortcut) {
                case 'today':
                    next = { startDate: untilYmd, endDate: untilYmd };
                    break;
                case '30_days':
                    next = {
                        startDate: format(subDays(newUntil, 30), 'yyyy-MM-dd'),
                        endDate: untilYmd,
                    };
                    break;
                case '60_days':
                    next = {
                        startDate: format(subDays(newUntil, 60), 'yyyy-MM-dd'),
                        endDate: untilYmd,
                    };
                    break;
                case '90_days':
                    next = {
                        startDate: format(subDays(newUntil, 90), 'yyyy-MM-dd'),
                        endDate: untilYmd,
                    };
                    break;
                default:
                    break;
            }
            if (next) {
                setDraftPeriod(next);
                setPeriodDates(next);
            }
        },
        [setDraftPeriod, setPeriodDates]
    );

    return {
        applyPeriod,
        handleCleaningFromDateChange,
        handleCleaningUntilDateChange,
        handleCleaningPeriodShortcut,
    };
}
